/**
 * Site Footer
 *
 * Shared public footer for the marketing surface.
 *
 * Primary responsibilities:
 * - Provide public navigation and support links
 * - Close the marketing shell with a stronger CTA and brand scene
 *
 * Dependencies:
 * - marketing/site-map
 * - marketing-motion
 * - next/dynamic
 */

'use client'

import Link from 'next/link'
import Image from 'next/image'
import dynamic from 'next/dynamic'
import { ArrowRightIcon, ArrowTopRightIcon, InstagramLogoIcon } from '@radix-ui/react-icons'
import { useTranslation } from 'react-i18next'

import { GeneratedMarketingAsset } from '@/components/marketing/generated-marketing-asset'
import { MarketingFooterField, MarketingReveal } from '@/components/marketing/marketing-motion'
import { AiReadinessLeadMagnetModal } from '@/components/quick-ai-readiness/lead-magnet-modal'
import {
  buildMarketingFooterGroups,
  buildMarketingPrimaryNavItems
} from '@/components/marketing/site-map'
import { Button } from '@/components/ui/button'
import { getMarketingCopy } from '@/lib/marketing-copy'
import { type SelectedGeneratedAssetKey } from '@/lib/marketing/selected-generated-assets'
import logo from '@/public/logo.png'

const footerSocials = [
  {
    href: 'https://www.instagram.com/asklosi',
    label: 'Instagram'
  }
]

const LanguageSwitcher = dynamic(
  () => import('@/components/languageswitcher/LanguageSwitcher'),
  {
    loading: () => (
      <div className="h-9 w-28 rounded-full border border-slate-200/80 bg-white/60 dark:border-white/10 dark:bg-white/5" />
    ),
    ssr: false
  }
)

export function SiteFooter() {
  const { i18n } = useTranslation()
  const copy = getMarketingCopy(i18n.resolvedLanguage || i18n.language)
  const marketingFooterGroups = buildMarketingFooterGroups(i18n.resolvedLanguage || i18n.language)
  const marketingPrimaryNavItems = buildMarketingPrimaryNavItems(
    i18n.resolvedLanguage || i18n.language
  )
  const footerSignals: Array<{
    assetKey: SelectedGeneratedAssetKey
    detail: string
    href: string
    label: string
  }> = [
    {
      href: '/login',
      label: 'Losi Chat',
      detail: copy.home.operatingModes[0].title,
      assetKey: 'footerSignalChat'
    },
    {
      href: '/features',
      label: 'Losi Spaces',
      detail: copy.home.operatingModes[2].title,
      assetKey: 'footerSignalSpaces'
    },
    {
      href: '/agents',
      label: 'Losi Nexus',
      detail: copy.home.nexus.points[0],
      assetKey: 'footerSignalNexus'
    }
  ]

  return (
    <footer className="px-4 pb-8 pt-10 sm:px-6 lg:px-8 lg:pb-10 lg:pt-12">
      <div className="mx-auto max-w-[1400px]">
        <div className="bg-white/82 relative overflow-hidden rounded-[2.2rem] border border-slate-200/80 p-6 shadow-[0_26px_80px_rgba(15,23,42,0.08)] backdrop-blur sm:p-8 lg:p-10 dark:border-white/10 dark:bg-[rgba(9,14,22,0.82)] dark:shadow-[0_26px_80px_rgba(0,0,0,0.34)]">
          <MarketingFooterField />

          <div className="relative z-10">
            <div className="grid gap-8 border-b border-slate-200/80 pb-10 lg:grid-cols-[1.04fr_0.96fr] lg:gap-10 dark:border-white/10">
              <MarketingReveal className="max-w-[38rem]" distance={24} blurPx={8}>
                <h2 className="font-display max-w-[11ch] text-4xl font-medium leading-[0.96] -tracking-wide text-slate-950 sm:text-5xl dark:text-slate-50">
                  {copy.footer.ctaTitle}
                </h2>
                <p className="mt-5 max-w-[34rem] text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
                  {copy.footer.ctaDescription}
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <Button
                    asChild
                    className="h-12 rounded-full bg-slate-950 px-6 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-white"
                  >
                    <Link href="/login">
                      {copy.common.openApp}
                      <ArrowRightIcon className="ml-2 size-4" />
                    </Link>
                  </Button>
                  <AiReadinessLeadMagnetModal source="marketing_footer">
                    <Button
                      variant="outline"
                      className="bg-white/88 h-12 rounded-full border-slate-300/90 px-6 text-slate-700 hover:bg-white dark:border-white/15 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
                    >
                      {copy.common.checkAiReadiness}
                    </Button>
                  </AiReadinessLeadMagnetModal>
                </div>
              </MarketingReveal>

              <MarketingReveal delay={0.1} distance={30} blurPx={10}>
                <div>
                  <div className="mb-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                    {copy.home.whySwitchLabel}
                  </div>

                  <div className="grid gap-3">
                    {footerSignals.map((signal, index) => (
                      <div
                        key={signal.label}
                        className="relative overflow-hidden rounded-[1.55rem] border border-slate-200/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.88),rgba(243,246,250,0.82))] p-5 pr-24 shadow-[0_14px_34px_rgba(15,23,42,0.06)] sm:pr-28 dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(15,22,32,0.86),rgba(9,14,22,0.8))] dark:shadow-[0_14px_34px_rgba(0,0,0,0.24)]"
                      >
                        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(177,192,214,0.28),transparent_44%)] dark:bg-[radial-gradient(circle_at_bottom_right,rgba(70,94,129,0.24),transparent_44%)]" />
                        <div className="pointer-events-none absolute -bottom-8 -right-6 size-36 opacity-75 sm:size-44 dark:opacity-90">
                          <GeneratedMarketingAsset
                            assetKey={signal.assetKey}
                            frame={false}
                            className="absolute inset-0"
                            imageClassName="object-contain object-bottom-right"
                            sizes="220px"
                          />
                        </div>

                        <div className="relative flex items-start gap-4">
                          <div className="flex size-10 shrink-0 items-center justify-center rounded-full border border-slate-200/90 bg-white text-[11px] font-semibold tracking-[0.2em] text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                            {`0${index + 1}`}
                          </div>

                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                              {signal.label}
                            </p>
                            <p className="mt-2 max-w-[24ch] text-sm leading-6 text-slate-700 dark:text-slate-200">
                              {signal.detail}
                            </p>
                          </div>

                          <Link
                            href={signal.href}
                            className="inline-flex size-10 shrink-0 items-center justify-center rounded-full border border-slate-200/90 bg-white text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-950 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:border-white/20 dark:hover:text-white"
                            aria-label={signal.label}
                          >
                            <ArrowTopRightIcon className="size-4" />
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </MarketingReveal>
            </div>

            <div className="grid gap-10 border-b border-slate-200/80 py-10 lg:grid-cols-[1.08fr_0.92fr] dark:border-white/10">
              <MarketingReveal className="max-w-[38rem]" distance={24} blurPx={8}>
                <div className="flex items-center gap-3">
                  <div className="flex size-12 items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-[inset_0_1px_0_rgba(255,255,255,0.92)] dark:border-white/10 dark:bg-white/5 dark:shadow-none">
                    <Image src={logo} alt="Losi" width={30} height={30} />
                  </div>
                  <div>
                    <div className="text-lg font-semibold tracking-tight text-slate-950 dark:text-slate-50">
                      Losi
                    </div>
                  </div>
                </div>

                <p className="mt-6 max-w-[34rem] text-base leading-8 text-slate-600 dark:text-slate-300">
                  {copy.footer.tagline}
                </p>

                <div className="mt-6 flex flex-wrap gap-2">
                  {marketingPrimaryNavItems.slice(0, 5).map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      className="bg-white/88 rounded-full border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:border-slate-300 hover:text-slate-950 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:border-white/20 dark:hover:text-white"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </MarketingReveal>

              <div className="grid gap-8 sm:grid-cols-3">
                {marketingFooterGroups.map((group, index) => (
                  <MarketingReveal
                    key={group.title}
                    delay={0.08 + index * 0.06}
                    distance={20}
                    blurPx={8}
                  >
                    <div>
                      <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                        {group.title}
                      </h3>
                      <div className="mt-4 grid gap-3">
                        {group.items.map((item) => {
                          const isExternal = item.external || item.href.startsWith('http')

                          return (
                            <Link
                              key={`${group.title}-${item.href}`}
                              href={item.href}
                              target={isExternal ? '_blank' : undefined}
                              rel={isExternal ? 'noopener noreferrer' : undefined}
                              className="flex items-center gap-2 text-sm text-slate-600 transition-colors hover:text-slate-950 dark:text-slate-300 dark:hover:text-white"
                            >
                              <span>{item.label}</span>
                              {isExternal ? <ArrowTopRightIcon className="size-3.5" /> : null}
                            </Link>
                          )
                        })}
                      </div>
                    </div>
                  </MarketingReveal>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-5 pt-6 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between dark:text-slate-400">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
                <span>
                  © {new Date().getFullYear()} Losi by{' '}
                  <Link
                    href="https://www.marshmallow.studio"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-slate-700 transition-colors hover:text-slate-950 dark:text-slate-300 dark:hover:text-white"
                  >
                    Marshmallow Studio
                  </Link>
                </span>

                <div className="flex items-center gap-4">
                  {footerSocials.map((social) => (
                    <Link
                      key={social.href}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-slate-600 transition-colors hover:text-slate-950 dark:text-slate-300 dark:hover:text-white"
                    >
                      <InstagramLogoIcon className="size-4" />
                      <span>{social.label}</span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="w-fit">
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
